import type {
  DesignBody,
  FaceKey,
  QuoteDesignViewFont,
  QuoteDesignViewInk,
  ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import {
  drawRasterToCanvas,
  FACE_LABELS,
  type FaceRenderDeps,
  useFaceRender,
} from '@repo/design-viewer';
import { type ReactElement, useEffect, useRef } from 'react';

/** 인쇄용이라 화면 뷰어(VIEWER_MAX_PIXELS)보다 작게 그려도 충분하다. */
const PRINT_MAX_PIXELS = 1_000_000;

interface ProductionOrderSheetFaceImageProps {
  readonly faceKey: FaceKey;
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly body: DesignBody;
  readonly assetUrls: ReadonlyMap<string, string>;
  readonly fonts: readonly QuoteDesignViewFont[];
  readonly inks: readonly QuoteDesignViewInk[];
  readonly refreshUrls: () => Promise<void>;
  /** 그리기가 끝났거나(성공) 실패했을 때 한 번 — 인쇄 시점을 잡는 데 쓴다. */
  readonly onSettled: () => void;
  readonly deps?: FaceRenderDeps;
}

/** 발주서에 싣는 한 면의 시안 — 브라우저에서 그려 캔버스에 옮기고 면 이름을 캡션으로 단다. */
export const ProductionOrderSheetFaceImage = ({
  faceKey,
  definition,
  colorOptionId,
  body,
  assetUrls,
  fonts,
  inks,
  refreshUrls,
  onSettled,
  deps,
}: ProductionOrderSheetFaceImageProps): ReactElement => {
  const { status, raster } = useFaceRender({
    enabled: true,
    maxPixels: PRINT_MAX_PIXELS,
    definition,
    colorOptionId,
    faceKey,
    body,
    assetUrls,
    fonts,
    inks,
    refreshUrls,
    deps,
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const settledRef = useRef(false);
  const label = FACE_LABELS[faceKey];
  const isReady = status === 'ready' && raster !== null;
  const isFailed = status === 'failed';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!isReady || canvas === null) return;
    try {
      drawRasterToCanvas(canvas, raster);
    } catch {
      // 옮기지 못해도 인쇄를 막지 않는다 — 빈 캔버스로 두고 끝낸 것으로 본다.
    }
  }, [isReady, raster]);

  useEffect(() => {
    if (settledRef.current || !(isReady || isFailed)) return;
    settledRef.current = true;
    onSettled();
  }, [isReady, isFailed, onSettled]);

  return (
    <figure className="m-0 flex w-full max-w-72 flex-col gap-1 print:break-inside-avoid">
      {isReady ? (
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${label} 시안`}
          className="h-auto w-full border border-foreground/20 print:border-black"
        />
      ) : (
        <p className="flex aspect-[4/3] items-center justify-center border border-dashed border-foreground/30 text-sm print:border-black">
          {isFailed ? '미리보기 준비 중' : ''}
        </p>
      )}
      <figcaption className="text-center text-xs">{label}</figcaption>
    </figure>
  );
};
